<script>
  import { format } from 'svelte-i18n';

  import { Chart } from '@client/components/charts';

  import {
    generateDataset,
    generateDomainsDataset,
    generateSenderRecipientDataset,
    getDomainDistribution,
    groupByEmailCounts,
    groupByTypes
  } from '@client/utils/audience';

  import { staticHexColors } from '@common/utils/colors';

  import { ChartCard } from '../chart-card';

  /**
   * @typedef {Object} ActivityEntry
   * @property {string} [recipient]
   * @property {string} [sender]
   * @property {string} [verb]
   * @property {Object} [note]

   * @typedef {Object} AudienceRow
   * @property {ActivityEntry[]} [activity]
   */

  /**
   * Audience data row, including raw activity.
   * @type {AudienceRow}
   */
  export let row = {};

  /**
   * Optional filter to narrow results to a specific recipient.
   * @type {string | null}
   */
  export let recipientFilter = null;

  /**
   * Whether to exclude sender-specific charts (insights mode).
   * @type {boolean}
   */
  export let isInsights = false;

  /**
   * Generates chart data based on current activity, filters, and mode.
   * @returns {Record<string, { labels: string[], datasets: any[] }>}
   */
  const generateChartData = () => {
    let activity = row.activity || [];

    if (recipientFilter) {
      activity = activity.filter(({ recipient }) => recipient === recipientFilter);
    }

    const data = groupByTypes(activity);

    let reports = {};

    if (!isInsights) {
      reports = {
        senders: generateSenderRecipientDataset(activity, 'sender')
      };
    }

    return {
      ...reports,
      recipients: generateSenderRecipientDataset(activity, 'recipient'),
      domains: generateDomainsDataset(getDomainDistribution(activity), 5),
      events: generateDataset(data.events, 5),
      countries: generateDataset(data.countries, 5),
      cities: generateDataset(data.cities, 5),
      browser: generateDataset(data.browser, 5),
      device: generateDataset(data.device, 5),
      os: generateDataset(data.os, 5)
    };
  };

  /**
   * Computed charts derived from current audience row data and recipient filter.
   * @type {ReturnType<typeof generateChartData>}
   */
  $: charts = generateChartData(recipientFilter, row);
</script>

<div class="audience">
  {#key recipientFilter}
    {#each Object.entries(charts) as [key, chart]}
      {#if chart.labels.length}
        <ChartCard typeKey={key} bind:chart />
      {/if}
    {/each}
  {/key}
</div>

<style lang="scss" src="./chart-grid.scss"></style>
